// Helpers for the candidate form's camera problems inside email apps.
//
// On phones, email apps often open links inside the app instead of a real browser:
//   - Android Gmail opens a Chrome Custom Tab. The browser identification string is the same
//     as normal Chrome, but camera permission prompts often fail or never show (mostly on
//     older Android). The only hint is document.referrer, which is often
//     "android-app://com.google.android.gm".
//   - Outlook, Yahoo, Facebook, LinkedIn, ... use a real WebView ("; wv)" on Android),
//     which usually has no camera at all.
// Veriff needs the camera, so we detect what we can, test the camera before Veriff starts,
// and offer to reopen the page in the phone's browser.

// Read once at startup: the SPA never changes document.referrer, but keep the original anyway.
const initialReferrer = typeof document !== 'undefined' ? (document.referrer || '') : ''

const ANDROID_APP_NAMES = {
    'com.google.android.gm': 'gmail',
    'com.microsoft.office.outlook': 'outlook',
    'com.yahoo.mobile.client.android.mail': 'yahoo_mail',
    'com.samsung.android.email.provider': 'samsung_email',
    'com.google.android.googlequicksearchbox': 'google_app',
    'com.facebook.katana': 'facebook',
    'com.facebook.orca': 'messenger',
    'com.instagram.android': 'instagram',
    'com.linkedin.android': 'linkedin',
    'com.whatsapp': 'whatsapp',
    'org.telegram.messenger': 'telegram',
}

// Apps that a browser should not be treated as "inside an app" when they appear as referrer.
const BROWSER_PACKAGES = [
    'com.android.chrome',
    'com.chrome.beta',
    'com.chrome.dev',
    'org.mozilla.firefox',
    'com.sec.android.app.sbrowser',
    'com.microsoft.emmx',
    'com.opera.browser',
    'com.brave.browser',
]

const UA_APP_PATTERNS = [
    [/FBAN|FBAV|FB_IAB/i, 'facebook'],
    [/Instagram/i, 'instagram'],
    [/LinkedInApp/i, 'linkedin'],
    [/\bLine\//i, 'line'],
    [/Snapchat/i, 'snapchat'],
    [/MicroMessenger/i, 'wechat'],
    [/Twitter/i, 'twitter'],
    [/\bGSA\//i, 'google_app'],
]

const getUserAgent = () => (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '')

export const getPlatform = () => {
    const ua = getUserAgent()
    if (/android/i.test(ua)) return 'android'
    if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
    // iPadOS reports itself as a Mac.
    if (/Macintosh/i.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1) return 'ios'
    return 'other'
}

export const isMobileDevice = () => getPlatform() !== 'other' || /Mobi/i.test(getUserAgent())

// Name of the app the page is running inside, or null when it looks like a normal browser.
// A Gmail Custom Tab is detected only through the referrer, so this can miss it — the camera
// check before Veriff is the reliable safety net.
export const detectInAppBrowser = () => {
    const ua = getUserAgent()

    const referrerMatch = /^android-app:\/\/([^/]+)/i.exec(initialReferrer)
    if (referrerMatch) {
        const pkg = referrerMatch[1].toLowerCase()
        if (!BROWSER_PACKAGES.includes(pkg)) {
            return ANDROID_APP_NAMES[pkg] || `app:${pkg}`.slice(0, 50)
        }
    }

    for (const [pattern, name] of UA_APP_PATTERNS) {
        if (pattern.test(ua)) return name
    }

    if (/android/i.test(ua) && /; wv\)/.test(ua)) return 'android_webview'

    // iOS in-app WebViews leave out the "Safari/" token that Safari and other iOS browsers send.
    if (getPlatform() === 'ios' && !/Safari\//.test(ua)) return 'ios_webview'

    return null
}

// A link that asks the phone to reopen the current page in a real browser, or null where
// there is no such trick (desktop, or iOS when not inside an app).
//   Android: an intent link to Chrome. If Chrome isn't installed, the fallback reloads the page.
//   iOS: the x-safari-https scheme (iOS 17+) opens Safari.
export const getOpenInBrowserUrl = () => {
    if (typeof window === 'undefined') return null
    const { href, host, pathname, search, hash, protocol } = window.location
    const scheme = protocol.replace(':', '')
    const platform = getPlatform()

    if (platform === 'android') {
        return `intent://${host}${pathname}${search}${hash}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`
    }

    if (platform === 'ios' && detectInAppBrowser()) {
        return `x-safari-${href}`
    }

    return null
}

export const copyCurrentLink = async () => {
    const text = window.location.href
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    }
    catch {
        // fall through to the old way (clipboard API is often missing in WebViews)
    }
    try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(textarea)
        return ok
    }
    catch {
        return false
    }
}

// How long to wait for the camera before giving up. Inside some email apps the permission
// prompt never appears and getUserMedia never answers.
const CAMERA_CHECK_TIMEOUT_MS = 20000

const mapCameraError = (name) => {
    switch (name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
        case 'SecurityError':
            return 'denied'
        case 'NotFoundError':
        case 'DevicesNotFoundError':
        case 'OverconstrainedError':
            return 'not_found'
        case 'NotReadableError':
        case 'TrackStartError':
        case 'AbortError':
            return 'in_use'
        default:
            return 'error'
    }
}

// Ask for the camera once, then release it right away. Granting it here also lets the Veriff
// frame use it without failing silently.
// Returns { result: "ok" | "denied" | "not_found" | "in_use" | "unsupported" | "timeout" | "error", errorName }
export const checkCameraAccess = () => {
    if (
        typeof window === 'undefined' ||
        !window.isSecureContext ||
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== 'function'
    ) {
        return Promise.resolve({ result: 'unsupported', errorName: null })
    }

    return new Promise((resolve) => {
        let settled = false

        const timer = setTimeout(() => {
            settled = true
            resolve({ result: 'timeout', errorName: null })
        }, CAMERA_CHECK_TIMEOUT_MS)

        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
            .then((stream) => {
                stream.getTracks().forEach((track) => track.stop())
                if (settled) return
                settled = true
                clearTimeout(timer)
                resolve({ result: 'ok', errorName: null })
            })
            .catch((err) => {
                if (settled) return
                settled = true
                clearTimeout(timer)
                const errorName = err?.name || 'Error'
                resolve({ result: mapCameraError(errorName), errorName })
            })
    })
}

// Snapshot sent to the backend (stored on the form tracking record) so we can see which
// apps and devices fail.
export const getClientEnvironment = (type, extra = {}) => ({
    type,
    userAgent: getUserAgent().slice(0, 1000),
    referrer: initialReferrer.slice(0, 500),
    inAppBrowser: detectInAppBrowser(),
    isMobile: isMobileDevice(),
    platform: getPlatform(),
    ...extra,
})
